import React from 'react';
import { useViewTickets } from './hooks/useViewTickets';

// Components
import SearchBar from './components/SearchBar';
import TicketsTable from './components/table/TicketsTable';
import Pagination from './components/table/Pagination';
import ExportModal from './components/modals/ExportModal';
import PasswordDialog from './components/modals/PasswordDialog';
import TicketDetailsModal from './components/modals/TicketDetailsModal';
import NotificationSnackbar from './components/NotificationSnackbar';

// Icons
import {
  KeyIcon,
  VolumeUpIcon,
  VolumeOffIcon,
  FileDownloadIcon
} from './components/icons/ViewTicketsIcons';

const ViewTickets = () => {
  const {
    // Main state
    tickets,
    loading,
    error,
    locationInfo,
    user,
    authLoading,
    authError,
    
    // Table state
    page,
    setPage,
    rowsPerPage,
    setRowsPerPage,
    filteredTickets,
    
    // Search and filter state
    searchQuery,
    setSearchQuery,
    selectedCategories,
    selectedStatuses,
    setSelectedStatuses,
    dateRange,
    setDateRange,
    
    // Modal state
    exportModalOpen,
    setExportModalOpen,
    selectedTicket,
    setSelectedTicket,
    viewDetailsOpen,
    setViewDetailsOpen,
    
    // Notification state
    soundEnabled,
    setSoundEnabled,
    notificationOpen,
    setNotificationOpen,
    notificationMessage,
    
    // Password state
    passwordDialogOpen,
    setPasswordDialogOpen,
    passwordData,
    setPasswordData,
    passwordError,
    setPasswordError,
    passwordSuccess,
    setPasswordSuccess,
    showPassword,
    setShowPassword,
    
    // Functions
    fetchTickets
  } = useViewTickets();

  const appVersion = import.meta.env.VITE_VERSION;

  // Event handlers
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };

  const handleClearSearch = () => {
    setSearchQuery('');
  };

  const handleExportModalOpen = () => {
    setExportModalOpen(true);
  };

  const handleExportModalClose = () => {
    setExportModalOpen(false);
  };

  const handlePasswordDialogOpen = () => {
    setPasswordDialogOpen(true);
    setPasswordError('');
    setPasswordSuccess('');
    setPasswordData({
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    });
  };

  const handlePasswordDialogClose = () => {
    setPasswordDialogOpen(false);
  };

  const handleToggleSound = () => {
    setSoundEnabled(prev => !prev);
  };

  const handleViewDetails = (ticket) => {
    setSelectedTicket(ticket);
    setViewDetailsOpen(true);
    // Store the selected ticket in localStorage for persistence
    localStorage.setItem('selectedTicket', JSON.stringify(ticket));
    localStorage.setItem('viewDetailsOpen', 'true');
  };

  const handleCloseDetails = () => {
    setViewDetailsOpen(false);
    // Clear the viewDetailsOpen flag but keep the ticket for reference
    localStorage.removeItem('viewDetailsOpen');
  };

  const handleNotificationClose = () => {
    setNotificationOpen(false);
  };

  if (loading) {
    return (
      <div className="flex flex-col justify-center items-center min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 font-poppins">
        <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mb-4"></div>
        <p className="text-lg text-gray-600 font-medium">
          Loading tickets...
        </p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 font-poppins">
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium">{error}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-gray-100 overflow-hidden relative animate-fade-in font-poppins">
      {/* Background gradient overlay */}
      <div className="absolute top-0 right-0 w-3/4 h-3/5 bg-gradient-radial from-blue-200/15 to-transparent z-0 pointer-events-none"></div>
      <div className="w-full mt-8 mx-4 mb-8 overflow-hidden rounded-lg border border-gray-200 bg-white/90 backdrop-blur-sm relative shadow-lg z-10">
        {/* Header */}
        <div className="p-6 border-b border-gray-200 flex flex-col sm:flex-row justify-between items-start sm:items-center bg-gray-50 relative overflow-hidden">
          {/* Background gradient overlay */}
          <div className="absolute -top-1/2 -right-1/4 w-80 h-80 bg-gradient-radial from-blue-200/8 to-transparent z-0"></div>
          
          <div className="relative z-10">
            <h2 className="text-xl font-semibold text-blue-600 font-poppins">
              ICT COOR Dashboard
            </h2>
            <p className="text-gray-600 mt-1 font-poppins">
              {locationInfo?.name || 'Unknown Location'} - {locationInfo?.type} {locationInfo?.level ? `- ${locationInfo.level}` : ''}
            </p>
          </div>
          
          <div className="flex flex-wrap items-center gap-3 mt-4 sm:mt-0 relative z-10">
            <button
              onClick={handlePasswordDialogOpen}
              className="flex items-center gap-2 px-4 py-2 border border-blue-600 text-blue-600 rounded-lg hover:bg-blue-50 hover:border-blue-700 transition-all duration-300 font-poppins text-sm font-medium"
            >
              <KeyIcon className="w-4 h-4" />
              Change Password
            </button>
            
            <button
              onClick={handleToggleSound}
              className="flex items-center gap-2 px-4 py-2 border border-blue-600 text-blue-600 rounded-lg hover:bg-blue-50 hover:border-blue-700 transition-all duration-300 font-poppins text-sm font-medium"
            >
              {soundEnabled ? <VolumeUpIcon className="w-4 h-4" /> : <VolumeOffIcon className="w-4 h-4" />}
              Sound {soundEnabled ? "On" : "Off"}
            </button>
            
            <button
              onClick={handleExportModalOpen}
              disabled={tickets.length === 0}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all duration-300 font-poppins text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl"
            >
              <FileDownloadIcon className="w-4 h-4" />
              Export to Excel
            </button>
          </div>
        </div>
        
        {/* Search Bar */}
        <SearchBar
          searchQuery={searchQuery}
          onSearchChange={handleSearchChange}
          onClearSearch={handleClearSearch}
        />

        {/* Table */}
        <TicketsTable
          filteredTickets={filteredTickets}
          page={page}
          rowsPerPage={rowsPerPage}
          searchQuery={searchQuery}
          onViewDetails={handleViewDetails}
        />
        
        {/* Pagination */}
        <Pagination
          page={page}
          rowsPerPage={rowsPerPage}
          filteredTickets={filteredTickets}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />

      </div>
      
      {/* Modals - moved outside container to avoid overflow constraints */}
      <ExportModal
        isOpen={exportModalOpen}
        onClose={handleExportModalClose}
        tickets={tickets}
        locationInfo={locationInfo}
        dateRange={dateRange}
        setDateRange={setDateRange}
        selectedStatuses={selectedStatuses}
        setSelectedStatuses={setSelectedStatuses}
      />

      <PasswordDialog
        isOpen={passwordDialogOpen}
        onClose={handlePasswordDialogClose}
        passwordData={passwordData}
        setPasswordData={setPasswordData}
        passwordError={passwordError}
        setPasswordError={setPasswordError}
        passwordSuccess={passwordSuccess}
        setPasswordSuccess={setPasswordSuccess}
        showPassword={showPassword}
        setShowPassword={setShowPassword}
      />

      <TicketDetailsModal
        isOpen={viewDetailsOpen}
        onClose={handleCloseDetails}
        selectedTicket={selectedTicket}
        locationInfo={locationInfo}
      />

      {/* Notification */}
      <NotificationSnackbar
        isOpen={notificationOpen}
        message={notificationMessage}
        onClose={handleNotificationClose}
      />
      
      {/* Version display in footer */}
      <div className="w-full text-center mt-auto mb-4 py-4">
        <p className="text-xs sm:text-sm text-gray-500 italic font-poppins">
          version {appVersion}
        </p>
      </div>
    </div>
  );
};

export default ViewTickets;